import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listarZenGestaoEmpresas,
  criarZenGestaoEmpresa,
  inativarZenGestaoEmpresa,
  atualizarZenGestaoEmpresa,
} from '../services/api';

const ZenGestaoEmpresasSection = ({
  token,
  zenGestaoId,
  empresasAdicionaisLimite = 0,
  reloadKey = 0,
}) => {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState({
    razaoSocial: '',
    nomeFantasia: '',
    cnpj: '',
  });

  const carregar = useCallback(async () => {
    if (!zenGestaoId) return;
    setLoading(true);
    try {
      const { data } = await listarZenGestaoEmpresas(zenGestaoId, token);
      setEmpresas(data?.empresas || []);
    } catch (err) {
      console.error('Erro ao carregar empresas:', err);
    } finally {
      setLoading(false);
    }
  }, [zenGestaoId, token]);

  useEffect(() => {
    carregar();
  }, [carregar, reloadKey]);

  const ativas = useMemo(
    () => empresas.filter((e) => e.ativo !== false),
    [empresas]
  );
  const inativas = useMemo(
    () => empresas.filter((e) => e.ativo === false),
    [empresas]
  );

  const limite = Math.max(0, Number(empresasAdicionaisLimite) || 0);
  const vagasDisponiveis = Math.max(0, limite - ativas.length);
  const excedentesAoSalvar = Math.max(0, ativas.length - limite);

  const handleAdicionar = async (e) => {
    e.preventDefault();
    if (!zenGestaoId || vagasDisponiveis <= 0) return;
    setSalvando(true);
    try {
      await criarZenGestaoEmpresa(zenGestaoId, { ...form, ativo: true }, token);
      setForm({ razaoSocial: '', nomeFantasia: '', cnpj: '' });
      await carregar();
    } catch (err) {
      const msg = err?.response?.data?.mensagem || 'Não foi possível cadastrar a empresa.';
      alert(msg);
    } finally {
      setSalvando(false);
    }
  };

  const handleInativar = async (empresa) => {
    if (!window.confirm(`Inativar a empresa ${empresa.nomeFantasia || empresa.razaoSocial}?`)) return;
    try {
      await inativarZenGestaoEmpresa(zenGestaoId, empresa.id, token);
      await carregar();
    } catch (err) {
      alert(err?.response?.data?.mensagem || 'Erro ao inativar empresa.');
    }
  };

  const handleReativar = async (empresa) => {
    if (vagasDisponiveis <= 0) {
      alert('Não há vagas disponíveis. Aumente o contador de empresa adicional na licença.');
      return;
    }
    try {
      await atualizarZenGestaoEmpresa(
        zenGestaoId,
        empresa.id,
        {
          razaoSocial: empresa.razaoSocial,
          nomeFantasia: empresa.nomeFantasia,
          cnpj: empresa.cnpj,
          ativo: true,
        },
        token
      );
      await carregar();
    } catch (err) {
      alert(err?.response?.data?.mensagem || 'Erro ao reativar empresa.');
    }
  };

  if (!zenGestaoId) return null;

  if (limite === 0 && empresas.length === 0) {
    return null;
  }

  return (
    <div className="zen-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10 space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Empresas adicionais
        </h3>
        <p className="text-sm text-gray-500 dark:text-slate-300/80">
          Limite configurado: {limite} empresa(s) adicional(is) (1 empresa base + contador ADD_EMPRESA).
          {limite > 0 && (
            <span className="block mt-1">
              Vagas disponíveis: <strong>{vagasDisponiveis}</strong> de {limite}.
            </span>
          )}
        </p>
      </div>

      {excedentesAoSalvar > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-50/80 dark:bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-100">
          Ao salvar a licença, {excedentesAoSalvar} empresa(s) adicional(is) ativa(s) será(ão)
          inativada(s) automaticamente (mantidas as mais antigas dentro do limite).
        </div>
      )}

      {limite === 0 && ativas.length > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-50/80 dark:bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-100">
          O contador de empresa adicional está em 0. Salve a licença para inativar as empresas
          adicionais ativas.
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Carregando empresas...</p>
      ) : (
        <>
          {ativas.length === 0 ? (
            <p className="text-sm text-gray-500">Nenhuma empresa adicional ativa.</p>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-white/10">
              {ativas.map((e) => (
                <li key={e.id} className="py-2 flex items-center justify-between gap-3">
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {e.nomeFantasia || e.razaoSocial}
                    </div>
                    <div className="text-xs text-gray-500">{e.cnpj}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleInativar(e)}
                    className="text-sm text-red-600 hover:underline"
                  >
                    Inativar
                  </button>
                </li>
              ))}
            </ul>
          )}

          {inativas.length > 0 && (
            <div className="pt-2">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                Inativas
              </h4>
              <ul className="divide-y divide-gray-200 dark:divide-white/10">
                {inativas.map((e) => (
                  <li key={e.id} className="py-2 flex items-center justify-between gap-3 opacity-70">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {e.nomeFantasia || e.razaoSocial}
                      </div>
                      <div className="text-xs text-gray-500">{e.cnpj}</div>
                    </div>
                    {vagasDisponiveis > 0 ? (
                      <button
                        type="button"
                        onClick={() => handleReativar(e)}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Reativar
                      </button>
                    ) : (
                      <span className="text-xs text-gray-500">Sem vaga</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {limite > 0 && vagasDisponiveis > 0 && (
            <form onSubmit={handleAdicionar} className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
              <input
                className="px-3 py-2 rounded-xl border dark:bg-slate-950/40 dark:border-white/10"
                placeholder="Razão social"
                value={form.razaoSocial}
                onChange={(e) => setForm((f) => ({ ...f, razaoSocial: e.target.value }))}
                required
              />
              <input
                className="px-3 py-2 rounded-xl border dark:bg-slate-950/40 dark:border-white/10"
                placeholder="Nome fantasia"
                value={form.nomeFantasia}
                onChange={(e) => setForm((f) => ({ ...f, nomeFantasia: e.target.value }))}
              />
              <input
                className="px-3 py-2 rounded-xl border dark:bg-slate-950/40 dark:border-white/10"
                placeholder="CNPJ"
                value={form.cnpj}
                onChange={(e) => setForm((f) => ({ ...f, cnpj: e.target.value }))}
                required
              />
              <div className="md:col-span-3">
                <button
                  type="submit"
                  disabled={salvando}
                  className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold disabled:opacity-60"
                >
                  {salvando ? 'Salvando...' : `Adicionar empresa (${vagasDisponiveis} vaga(s))`}
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
};

export default ZenGestaoEmpresasSection;
